import { useState } from "react";
import { api, ApiError, type GalleryPage, type UploadRecord } from "../lib/api";
import { copyToClipboard, formatBytes, formatDate, frameNumber } from "../lib/format";
import { useToast } from "./Toast";

interface Props {
  page: GalleryPage | null;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onChanged: () => void;
}

/**
 * The gallery, laid out as a mosaic.
 *
 * Images keep their own proportions and tile into columns, so the sheet reads
 * as a wall of pictures rather than a grid of cropped thumbnails. Chrome only
 * appears on hover or keyboard focus; at rest the images are the whole
 * interface. The frame number stays, carried over from the contact-sheet idea
 * that a filed image gets a mark in the margin.
 */
export function ContactSheet({
  page,
  isLoading,
  search,
  onSearchChange,
  onPageChange,
  onChanged,
}: Props) {
  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const pageSize = page?.page_size ?? 24;
  const currentPage = page?.page ?? 1;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="sheet" aria-label="Uploaded images">
      <header className="sheet__head">
        <span className="label">Contact sheet</span>
        <span className="label" aria-live="polite">
          {isLoading ? "Loading" : `${total} frame${total === 1 ? "" : "s"}`}
        </span>
        <div className="field sheet__search">
          <label className="label" htmlFor="sheet-search" hidden>
            Search by filename
          </label>
          <input
            id="sheet-search"
            type="text"
            placeholder="Search filenames"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
      </header>

      {items.length === 0 ? (
        <div className="sheet__empty">
          <span className="label">{search ? "No matches" : "Sheet is empty"}</span>
          <p>
            {search
              ? "No filename matches that search."
              : "Upload an image and it lands here with a link ready to copy."}
          </p>
        </div>
      ) : (
        <div className="mosaic">
          {items.map((item, index) => (
            <Frame
              key={item.id}
              record={item}
              number={frameNumber(index, currentPage, pageSize)}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}

      {lastPage > 1 && (
        <footer className="sheet__foot">
          <button
            className="btn btn--quiet"
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span className="label">
            Sheet {currentPage} of {lastPage}
          </span>
          <button
            className="btn btn--quiet"
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= lastPage}
          >
            Next
          </button>
        </footer>
      )}
    </section>
  );
}

function Frame({
  record,
  number,
  onChanged,
}: {
  record: UploadRecord;
  number: string;
  onChanged: () => void;
}) {
  const { notify } = useToast();
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isWorking, setIsWorking] = useState(false);

  async function handleCopy() {
    const copied = await copyToClipboard(record.url);
    notify(copied ? "Link copied." : "Could not reach the clipboard.", copied ? "success" : "error");
  }

  // A direct link can stop resolving if the storage policy changes on the
  // Cloudreve side; re-minting it is cheaper than re-uploading.
  async function handleRefreshLink() {
    setIsWorking(true);
    try {
      await api.uploads.refreshLink(record.id);
      setImageFailed(false);
      notify("Link refreshed.", "success");
      onChanged();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not refresh the link.", "error");
    } finally {
      setIsWorking(false);
    }
  }

  async function handleRemove() {
    if (
      !window.confirm(`Remove "${record.file_name}" from the sheet?\n\nThe file stays in Cloudreve.`)
    ) {
      return;
    }
    setIsWorking(true);
    try {
      await api.gallery.remove(record.id);
      notify("Removed from the sheet.", "info");
      onChanged();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not remove it.", "error");
      setIsWorking(false);
    }
  }

  return (
    // data-loaded drives a placeholder aspect ratio in CSS. Without it a tile
    // has no height until its image arrives, and in a multi-column layout a
    // zero-height tile never counts as near the viewport, so a lazy image would
    // never start loading and the tile would never gain height.
    <figure className="tile" data-loaded={imageLoaded}>
      {imageFailed ? (
        <div className="tile__missing">
          <span className="label">Preview unavailable</span>
        </div>
      ) : (
        <img
          className="tile__image"
          src={record.url}
          alt={record.file_name}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
        />
      )}

      <span className="tile__number" aria-hidden="true">
        {number}
      </span>

      <figcaption className="tile__overlay">
        <div className="tile__meta">
          <span className="tile__name" title={record.file_name}>
            {record.file_name}
          </span>
          <span className="tile__sub">
            {formatBytes(record.size_bytes)} · {record.source === "api" ? "API" : "Web"} ·{" "}
            {formatDate(record.created_at)}
          </span>
        </div>

        <div className="tile__actions">
          <button className="btn" type="button" onClick={handleCopy}>
            Copy link
          </button>
          <button
            className="btn"
            type="button"
            onClick={handleRefreshLink}
            disabled={isWorking}
            title="Ask Cloudreve for a new direct link"
          >
            Relink
          </button>
          <button
            className="btn btn--danger"
            type="button"
            onClick={handleRemove}
            disabled={isWorking}
            title="Remove from this sheet; the file stays in Cloudreve"
          >
            Remove
          </button>
        </div>
      </figcaption>
    </figure>
  );
}
